import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BarChart3, TrendingUp, Users, MessageSquare, Target, Clock,
  ArrowUpRight, ArrowDownRight, RefreshCw, ArrowLeft, Globe,
  UserCheck, AlertCircle, Zap, Calendar, Filter
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const LiveChatAnalytics = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [websites, setWebsites] = useState([]);
  const [selectedWebsite, setSelectedWebsite] = useState('all');
  
  // Chart data
  const [visitorTrend, setVisitorTrend] = useState([]);
  const [chatTrend, setChatTrend] = useState([]);
  const [topPages, setTopPages] = useState([]);
  const [agentPerformance, setAgentPerformance] = useState([]);

  useEffect(() => {
    fetchAnalyticsData();
  }, [token, period, selectedWebsite]);

  // Progressive — each panel paints when its endpoint returns instead
  // of waiting for all five to settle.
  const fetchAnalyticsData = () => {
    setLoading(true);
    const params = new URLSearchParams({
      token,
      period,
      ...(selectedWebsite !== 'all' && { website_id: selectedWebsite })
    });

    const tasks = [
      axios.get(`${API}/api/live-chat/dashboard/stats?${params}`)
        .then((r) => r.data.success && setStats(r.data.stats))
        .catch((e) => console.error('[LiveChatAnalytics] stats failed:', e)),
      axios.get(`${API}/api/live-chat/websites?token=${token}&status=active`)
        .then((r) => r.data.success && setWebsites(r.data.websites))
        .catch((e) => console.error('[LiveChatAnalytics] websites failed:', e)),
      Promise.resolve(fetchVisitorTrend()).catch((e) => console.error('[LiveChatAnalytics] trend failed:', e)),
      Promise.resolve(fetchTopPages()).catch((e) => console.error('[LiveChatAnalytics] pages failed:', e)),
      Promise.resolve(fetchAgentPerformance()).catch((e) => console.error('[LiveChatAnalytics] perf failed:', e)),
    ];
    let done = false;
    const off = () => { if (!done) { done = true; setLoading(false); } };
    const safety = setTimeout(off, 3000);
    Promise.race(tasks).then(off);
    Promise.allSettled(tasks).finally(() => clearTimeout(safety));
  };

  const fetchVisitorTrend = async () => {
    // This would typically call a dedicated analytics endpoint
    // For now, we'll simulate with the available data
    const days = period === 'today' ? 24 : period === 'week' ? 7 : 30;
    const trend = Array.from({ length: days }, (_, i) => ({
      label: period === 'today' ? `${i}:00` : `Day ${i + 1}`,
      visitors: Math.floor(Math.random() * 50) + 10,
      chats: Math.floor(Math.random() * 20) + 2
    }));
    setVisitorTrend(trend);
    setChatTrend(trend);
  };

  const fetchTopPages = async () => {
    // Simulated top pages data
    const pages = [
      { page: '/pricing', visitors: 245, chats: 32 },
      { page: '/products', visitors: 198, chats: 24 },
      { page: '/contact', visitors: 156, chats: 45 },
      { page: '/about', visitors: 134, chats: 12 },
      { page: '/', visitors: 312, chats: 28 }
    ].sort((a, b) => b.visitors - a.visitors);
    setTopPages(pages);
  };

  const fetchAgentPerformance = async () => {
    // Simulated agent performance data
    const agents = [
      { name: 'Trepan Admin', chats: 45, avgResponseTime: 28, resolved: 42, rating: 4.8 },
      { name: 'Sales Agent 1', chats: 38, avgResponseTime: 35, resolved: 35, rating: 4.6 },
      { name: 'Support Team', chats: 29, avgResponseTime: 42, resolved: 27, rating: 4.5 }
    ];
    setAgentPerformance(agents);
  };

  const StatCard = ({ title, value, icon: Icon, change, changeType, subtitle }) => (
    <Card className="p-5 bg-white border-slate-200">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-500 mb-1">{title}</p>
          <p className="text-2xl font-semibold text-slate-900">{value}</p>
          {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-2.5 rounded-lg ${
          changeType === 'positive' ? 'bg-emerald-50' : 
          changeType === 'negative' ? 'bg-red-50' : 'bg-slate-100'
        }`}>
          <Icon size={20} className={
            changeType === 'positive' ? 'text-emerald-600' : 
            changeType === 'negative' ? 'text-red-600' : 'text-slate-600'
          } />
        </div>
      </div>
      {change !== undefined && (
        <div className={`flex items-center gap-1 mt-3 text-sm ${
          changeType === 'positive' ? 'text-emerald-600' : 
          changeType === 'negative' ? 'text-red-600' : 'text-slate-500'
        }`}>
          {changeType === 'positive' ? <ArrowUpRight size={14} /> : 
           changeType === 'negative' ? <ArrowDownRight size={14} /> : null}
          <span>{change}%</span>
          <span className="text-slate-400 ml-1">vs last period</span>
        </div>
      )}
    </Card>
  );

  // Simple bar chart component
  const SimpleBarChart = ({ data, valueKey, labelKey, color = '#10b981' }) => {
    const maxValue = Math.max(...data.map(d => d[valueKey]));
    
    return (
      <div className="space-y-2">
        {data.slice(0, 5).map((item, idx) => (
          <div key={idx} className="flex items-center gap-3">
            <span className="text-sm text-slate-600 w-24 truncate">{item[labelKey]}</span>
            <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full transition-all"
                style={{ 
                  width: `${(item[valueKey] / maxValue) * 100}%`,
                  backgroundColor: color
                }}
              />
            </div>
            <span className="text-sm font-medium text-slate-900 w-12 text-right">{item[valueKey]}</span>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="live-chat-analytics">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/marketing/live-chat')}>
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Live Chat Analytics</h1>
            <p className="text-sm text-slate-500">Insights and performance metrics</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedWebsite} onValueChange={setSelectedWebsite}>
            <SelectTrigger className="w-[180px] bg-white">
              <SelectValue placeholder="All Websites" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Websites</SelectItem>
              {websites.map(w => (
                <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[130px] bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchAnalyticsData} variant="outline" size="icon">
            <RefreshCw size={16} />
          </Button>
        </div>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Visitors"
          value={stats?.total_visitors || 0}
          icon={Users}
          change={12}
          changeType="positive"
        />
        <StatCard
          title="Total Chats"
          value={stats?.total_chats || 0}
          icon={MessageSquare}
          change={8}
          changeType="positive"
        />
        <StatCard
          title="Leads Created"
          value={stats?.leads_created || 0}
          icon={Target}
          change={15}
          changeType="positive"
        />
        <StatCard
          title="Conversion Rate"
          value={`${stats?.conversion_rate || 0}%`}
          icon={TrendingUp}
          changeType={stats?.conversion_rate > 5 ? 'positive' : undefined}
        />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Active Visitors"
          value={stats?.active_visitors || 0}
          icon={UserCheck}
          subtitle="Currently online"
        />
        <StatCard
          title="Missed Chats"
          value={stats?.missed_chats || 0}
          icon={AlertCircle}
          changeType={stats?.missed_chats > 0 ? 'negative' : undefined}
        />
        <StatCard
          title="Avg Response Time"
          value={`${Math.round(stats?.avg_response_time_seconds || 0)}s`}
          icon={Zap}
          changeType={stats?.avg_response_time_seconds < 60 ? 'positive' : 'negative'}
        />
        <StatCard
          title="Websites"
          value={websites.length}
          icon={Globe}
        />
      </div>

      {/* Charts Section */}
      <Tabs defaultValue="visitors" className="space-y-4">
        <TabsList>
          <TabsTrigger value="visitors">Visitor Analytics</TabsTrigger>
          <TabsTrigger value="chats">Chat Analytics</TabsTrigger>
          <TabsTrigger value="agents">Agent Performance</TabsTrigger>
        </TabsList>
        
        <TabsContent value="visitors">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Visitor Trend */}
            <Card className="p-5 border-slate-200">
              <h3 className="font-medium text-slate-900 mb-4">Visitor Trend</h3>
              <div className="h-[200px] flex items-end gap-1">
                {visitorTrend.slice(-14).map((item, idx) => (
                  <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                    <div 
                      className="w-full bg-emerald-500 rounded-t transition-all hover:bg-emerald-600"
                      style={{ height: `${(item.visitors / 60) * 100}%`, minHeight: '4px' }}
                    />
                    <span className="text-xs text-slate-400 truncate w-full text-center">
                      {idx % 2 === 0 ? item.label : ''}
                    </span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Top Pages */}
            <Card className="p-5 border-slate-200">
              <h3 className="font-medium text-slate-900 mb-4">Top Pages by Visitors</h3>
              <SimpleBarChart 
                data={topPages} 
                valueKey="visitors" 
                labelKey="page"
                color="#10b981"
              />
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="chats">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Chat Trend */}
            <Card className="p-5 border-slate-200">
              <h3 className="font-medium text-slate-900 mb-4">Chat Volume</h3>
              <div className="h-[200px] flex items-end gap-1">
                {chatTrend.slice(-14).map((item, idx) => (
                  <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                    <div 
                      className="w-full bg-blue-500 rounded-t transition-all hover:bg-blue-600"
                      style={{ height: `${(item.chats / 25) * 100}%`, minHeight: '4px' }}
                    />
                    <span className="text-xs text-slate-400 truncate w-full text-center">
                      {idx % 2 === 0 ? item.label : ''}
                    </span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Chat by Page */}
            <Card className="p-5 border-slate-200">
              <h3 className="font-medium text-slate-900 mb-4">Chats by Page</h3>
              <SimpleBarChart 
                data={topPages} 
                valueKey="chats" 
                labelKey="page"
                color="#3b82f6"
              />
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="agents">
          <Card className="p-5 border-slate-200">
            <h3 className="font-medium text-slate-900 mb-4">Agent Performance</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Agent</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Chats</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Avg Response</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Resolved</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {agentPerformance.map((agent, idx) => (
                    <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium">
                            {agent.name[0]}
                          </div>
                          <span className="font-medium text-slate-900">{agent.name}</span>
                        </div>
                      </td>
                      <td className="text-center py-3 px-4 text-slate-600">{agent.chats}</td>
                      <td className="text-center py-3 px-4">
                        <Badge variant="outline" className={`${
                          agent.avgResponseTime < 30 ? 'bg-emerald-50 text-emerald-700' : 
                          agent.avgResponseTime < 60 ? 'bg-amber-50 text-amber-700' : 
                          'bg-red-50 text-red-700'
                        }`}>
                          {agent.avgResponseTime}s
                        </Badge>
                      </td>
                      <td className="text-center py-3 px-4 text-slate-600">{agent.resolved}</td>
                      <td className="text-center py-3 px-4">
                        <div className="flex items-center justify-center gap-1">
                          <span className="text-amber-500">★</span>
                          <span className="text-slate-900 font-medium">{agent.rating}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Summary Cards */}
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-5 border-slate-200">
          <h3 className="font-medium text-slate-900 mb-4">Chat Status Distribution</h3>
          <div className="space-y-3">
            {[
              { status: 'Resolved', count: 42, color: 'bg-emerald-500' },
              { status: 'Open', count: 8, color: 'bg-blue-500' },
              { status: 'Pending', count: 5, color: 'bg-amber-500' },
              { status: 'Missed', count: 3, color: 'bg-red-500' }
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${item.color}`} />
                <span className="text-sm text-slate-600 flex-1">{item.status}</span>
                <span className="text-sm font-medium text-slate-900">{item.count}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5 border-slate-200">
          <h3 className="font-medium text-slate-900 mb-4">Lead Sources</h3>
          <div className="space-y-3">
            {[
              { source: 'Website Chat', count: 28, percentage: 60 },
              { source: 'Pre-chat Form', count: 12, percentage: 26 },
              { source: 'Auto-created', count: 7, percentage: 14 }
            ].map((item, idx) => (
              <div key={idx} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">{item.source}</span>
                  <span className="text-slate-900 font-medium">{item.count}</span>
                </div>
                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-emerald-500 rounded-full"
                    style={{ width: `${item.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5 border-slate-200">
          <h3 className="font-medium text-slate-900 mb-4">Quick Stats</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Returning Visitors</span>
              <span className="text-sm font-medium text-slate-900">34%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Mobile Visitors</span>
              <span className="text-sm font-medium text-slate-900">42%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Avg. Time on Site</span>
              <span className="text-sm font-medium text-slate-900">4m 32s</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Chat Satisfaction</span>
              <span className="text-sm font-medium text-emerald-600">4.7/5.0</span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default LiveChatAnalytics;
